/**
 * Shared color constants for tags and other UI elements
 *
 * This file serves as the single source of truth for the standardized
 * color palette used throughout the application for tags and related components.
 */

// Define the color mapping structure
export interface ColorMapping {
  background: string;
  text: string;
  label: string;
  primary: string;
}

// Define the standardized color palette
export const STANDARDIZED_COLOR_PALETTE: Record<string, ColorMapping> = {
  pink: {
    background: '#FFD8DF', // pink-20 (lighter for tag background)
    text: '#8D3545',
    label: 'pink-20',
    primary: '#FFB2C1' // pink-30 (for color picker selection)
  },
  red: {
    background: '#FFD9D9', // red-20 (lighter for tag background)
    text: '#9C2628',
    label: 'red-20',
    primary: '#F98989' // red-40 (for color picker selection)
  },
  orange: {
    background: '#FFDACE', // orange-20 (lighter for tag background)
    text: '#92331E',
    label: 'orange-20',
    primary: '#FFB59D' // orange-30 (for color picker selection)
  },
  yellow: {
    background: '#FFE4AE', // yellow-20 (lighter for tag background)
    text: '#744A0B',
    label: 'yellow-20',
    primary: '#F0C355' // yellow-30 (for color picker selection)
  },
  green: {
    background: '#C3EAD1', // green-20 (lighter for tag background)
    text: '#0C612C',
    label: 'green-20',
    primary: '#90D6AA' // green-30 (for color picker selection)
  },
  teal: {
    background: '#A9EFE8', // teal-20 (lighter for tag background)
    text: '#165C56',
    label: 'teal-20',
    primary: '#46BBB0' // teal-40 (for color picker selection)
  },
  blue: {
    background: '#C6E6FF', // blue-20 (lighter for tag background)
    text: '#005693',
    label: 'blue-20',
    primary: '#82CFFF' // blue-30 (for color picker selection)
  },
  purple: {
    background: '#E7DDFC', // purple-20 (lighter for tag background)
    text: '#643AAA',
    label: 'purple-20',
    primary: '#D2BDF9' // purple-30 (for color picker selection)
  }
};

// Map old hex colors to standardized color names
export const OLD_HEX_TO_COLOR_NAME: Record<string, string> = {
  '#FFB2C1': 'pink',
  '#FF7D96': 'pink', // Legacy pink color
  '#F98989': 'red',
  '#EB4B4B': 'red', // Legacy red color
  '#FFB59D': 'orange',
  '#FF785A': 'orange', // Legacy orange color
  '#F0C355': 'yellow',
  '#EA9921': 'yellow', // Legacy yellow color
  '#E99921': 'yellow', // Alternative legacy yellow
  '#90D6AA': 'green',
  '#5EBB7F': 'green', // Legacy green color
  '#46BBB0': 'teal',
  '#82CFFF': 'blue',
  '#31B5F2': 'blue', // Legacy blue color
  '#D2BDF9': 'purple',
  '#BA99F6': 'purple' // Legacy purple color
};

// Common color name variations that might come from the API
export const COLOR_NAME_MAPPING: Record<string, string> = {
  pink: 'pink',
  red: 'red',
  orange: 'orange',
  yellow: 'yellow',
  green: 'green',
  teal: 'teal',
  cyan: 'teal',
  blue: 'blue',
  purple: 'purple',
  violet: 'purple',
  magenta: 'pink'
};

/**
 * Maps an API color to a standardized color palette entry
 * Handles both hex colors and color names
 */
export const mapToStandardColor = (apiColor: string): ColorMapping => {
  const normalizedColor = apiColor.toLowerCase().trim();

  // 1. Try exact hex match first (case-insensitive, whitespace trimmed)
  const upperCaseColor = apiColor.trim().toUpperCase();
  if (OLD_HEX_TO_COLOR_NAME[upperCaseColor]) {
    const colorName = OLD_HEX_TO_COLOR_NAME[upperCaseColor];
    return STANDARDIZED_COLOR_PALETTE[colorName];
  }

  // 2. Try color name mapping
  if (COLOR_NAME_MAPPING[normalizedColor]) {
    const colorName = COLOR_NAME_MAPPING[normalizedColor];
    return STANDARDIZED_COLOR_PALETTE[colorName];
  }

  // 3. Fallback to blue for any unknown colors
  return STANDARDIZED_COLOR_PALETTE.blue;
};

/**
 * Maps any incoming color (old API colors or background colors) to the primary color
 * used for color picker display and selection
 */
export const mapToPickerColor = (incomingColor: string | undefined): string => {
  if (!incomingColor) {
    return STANDARDIZED_COLOR_PALETTE.pink.primary; // Default fallback
  }

  const upperCaseColor = incomingColor.trim().toUpperCase();

  // Map from old API colors to primary colors
  const apiToPickerMap: Record<string, string> = {
    '#FF7D96': '#FFB2C1', // pink-40 → pink-30
    '#EB4B4B': '#F98989', // red-50 → red-40 (fixed: was #EB4B4C)
    '#FF785A': '#FFB59D', // orange-40 → orange-30
    '#EA9921': '#F0C355', // yellow-40 → yellow-30
    '#5EBB7F': '#90D6AA', // green-40 → green-30
    '#46BBB0': '#46BBB0', // teal-40 → teal-40 (same)
    '#31B5F2': '#82CFFF', // blue-40 → blue-30
    '#BA99F6': '#D2BDF9' // purple-40 → purple-30
  };

  // Map from background colors to primary colors
  const backgroundToPickerMap: Record<string, string> = {
    '#FFD8DF': '#FFB2C1', // pink-20 → pink-30
    '#FFD9D9': '#F98989', // red-20 → red-40
    '#FFDACE': '#FFB59D', // orange-20 → orange-30
    '#FFE4AE': '#F0C355', // yellow-20 → yellow-30
    '#C3EAD1': '#90D6AA', // green-20 → green-30
    '#A9EFE8': '#46BBB0', // teal-20 → teal-40
    '#C6E6FF': '#82CFFF', // blue-20 → blue-30
    '#E7DDFC': '#D2BDF9' // purple-20 → purple-30
  };

  // Check API color mapping first
  if (apiToPickerMap[upperCaseColor]) {
    return apiToPickerMap[upperCaseColor];
  }

  // Check background color mapping
  if (backgroundToPickerMap[upperCaseColor]) {
    return backgroundToPickerMap[upperCaseColor];
  }

  // If already a primary color, return as-is
  const primaryColors = Object.values(STANDARDIZED_COLOR_PALETTE).map(c =>
    c.primary.toUpperCase()
  );
  if (primaryColors.includes(upperCaseColor)) {
    return incomingColor;
  }

  // Fallback to blue primary
  return STANDARDIZED_COLOR_PALETTE.blue.primary;
};

/**
 * Maps a primary color (from picker) back to the background color for storage
 */
export const mapToStorageColor = (primaryColor: string): string => {
  const upperCaseColor = primaryColor.trim().toUpperCase();

  const primaryToBackgroundMap: Record<string, string> = {
    '#FFB2C1': '#FFD8DF', // pink-30 → pink-20
    '#F98989': '#FFD9D9', // red-40 → red-20
    '#FFB59D': '#FFDACE', // orange-30 → orange-20
    '#F0C355': '#FFE4AE', // yellow-30 → yellow-20
    '#90D6AA': '#C3EAD1', // green-30 → green-20
    '#46BBB0': '#A9EFE8', // teal-40 → teal-20
    '#82CFFF': '#C6E6FF', // blue-30 → blue-20
    '#D2BDF9': '#E7DDFC' // purple-30 → purple-20
  };

  return primaryToBackgroundMap[upperCaseColor] || primaryColor;
};

/**
 * Reverse maps a standardized color palette primary color back to the API format
 * Used when saving tags to convert from the new color system back to the old format
 */
export const mapFromStandardColor = (primaryColor: string): string => {
  // Map from UI colors (background/primary) to API colors (darker variants)
  const uiToApiColorMap: Record<string, string> = {
    '#FFD8DF': '#FF7D96', // pink-20 → pink-40
    '#FFD9D9': '#EB4B4B', // red-20 → red-50 (fixed: was #EB4B4C)
    '#FFDACE': '#FF785A', // orange-20 → orange-40
    '#FFE4AE': '#EA9921', // yellow-20 → yellow-40
    '#C3EAD1': '#5EBB7F', // green-20 → green-40
    '#A9EFE8': '#46BBB0', // teal-20 → teal-40
    '#C6E6FF': '#31B5F2', // blue-20 → blue-40
    '#E7DDFC': '#BA99F6' // purple-20 → purple-40
  };

  // Check if the primary color matches a UI color that needs mapping
  const apiColor = uiToApiColorMap[primaryColor.toUpperCase()];
  if (apiColor) {
    return apiColor;
  }
  // If no mapping found, return the original color (fallback)
  return primaryColor;
};

export const colorsMatch = (color1: string, color2: string) =>
  mapToPickerColor(color1) === mapToPickerColor(color2);
